/**
 * Permission Helpers
 * Centralized permission resolution for follower/friend access.
 * Uses OR logic: if EITHER follower perms OR friend perms allow it, access is granted.
 */

export interface FollowerPermissions {
  showDetailedAnalysis?: boolean;
  allowTailParlays?: boolean;
  showPropBreakdown?: boolean;
  showRecentBets?: boolean;
  showActivityFeed?: boolean;
  showFinancialDetails?: boolean;
}

export type FriendPermissions = FollowerPermissions;

export type PermissionKey = keyof FollowerPermissions;

/**
 * Check if a viewer can access a specific permission on another user's profile.
 *
 * Resolution order:
 * 1. Own profile → always true
 * 2. Following AND follower perms allow it → true
 * 3. Friend AND friend perms allow it → true
 * 4. Otherwise → false
 */
export function canViewContent(
  permission: PermissionKey,
  ctx: {
    isOwnProfile: boolean;
    isFollowing: boolean;
    isFriend: boolean;
    followerPerms?: FollowerPermissions | null;
    friendPerms?: FriendPermissions | null;
  },
): boolean {
  if (ctx.isOwnProfile) return true;
  if (ctx.isFollowing && ctx.followerPerms?.[permission] === true) return true;
  if (ctx.isFriend && ctx.friendPerms?.[permission] === true) return true;
  return false;
}

/** The perks the profile's follower-perks card lists and counts, in its order (web and mobile). */
export const FOLLOWER_BENEFITS: ReadonlyArray<{
  key: PermissionKey;
  label: string;
  description: string;
}> = [
  {
    key: 'showDetailedAnalysis',
    label: 'Detailed AI analysis',
    description: 'Per-leg breakdown, strategy, market context',
  },
  {
    key: 'allowTailParlays',
    label: 'Tail parlays',
    description: 'Copy their picks to your builder',
  },
  {
    key: 'showPropBreakdown',
    label: 'Prop-type breakdown',
    description: 'Detailed stats by prop type',
  },
  { key: 'showRecentBets', label: 'Full bet history', description: 'See all their tracked bets' },
  {
    key: 'showActivityFeed',
    label: 'New bet alerts',
    description: 'Get notified when they track or settle a parlay',
  },
  {
    key: 'showFinancialDetails',
    label: 'Stakes and profit',
    description: 'Profit/loss totals, plus stakes and payouts on shared bets',
  },
];

/** Whether a follower would get this perk. Bet alerts also need recent bets: the server sends none without them. */
export function isFollowerBenefitShared(
  key: PermissionKey,
  permissions: FollowerPermissions | null | undefined,
): boolean {
  if (key === 'showActivityFeed') {
    return permissions?.showActivityFeed === true && permissions.showRecentBets === true;
  }
  return permissions?.[key] === true;
}

export function countSharedFollowerBenefits(
  permissions: FollowerPermissions | null | undefined,
): number {
  return FOLLOWER_BENEFITS.filter((benefit) => isFollowerBenefitShared(benefit.key, permissions))
    .length;
}

/** The words and Follow state of the profile's follower-perks card for this viewer (web and mobile). */
export interface FollowerBenefitsCard {
  heading: string;
  notSharedNote: string;
  followLabel: string;
  isFollowDisabled: boolean;
}

/** An admin sees every stat already (Bryan, 2026-11-02), so their card says what followers get. */
export function followerBenefitsCard(
  permissions: FollowerPermissions | null | undefined,
  isAdminViewer: boolean,
): FollowerBenefitsCard {
  if (isAdminViewer) {
    return {
      heading: 'What followers get',
      notSharedNote: '(hidden from followers)',
      followLabel: 'Follow',
      isFollowDisabled: false,
    };
  }
  const sharedCount = countSharedFollowerBenefits(permissions);
  return {
    heading: `Follow to unlock ${sharedCount} benefit${sharedCount === 1 ? '' : 's'}`,
    notSharedNote: '(not shared)',
    followLabel: 'Follow to Unlock',
    isFollowDisabled: sharedCount === 0,
  };
}
